// Typography System - Centralized font styles

export const FONT_FAMILY = {
  primary: 'Lato, sans-serif',
  fallback: 'sans-serif',
} as const;

export const FONT_SIZE = {
  // Display sizes (large headings)
  display: {
    xl: '48px',    // Hero titles, major headings
    lg: '42px',    // Section titles
    md: '36px',    // Large headings
  },
  // Heading sizes
  heading: {
    xl: '32px',    // H1, page titles
    lg: '28px',    // H2, section headers
    md: '24px',    // H3, subsection headers
    sm: '20px',    // H4, card titles
    xs: '18px',    // H5, small headings
  },
  // Body text sizes
  body: {
    lg: '18px',    // Large body text, intro paragraphs
    md: '16px',    // Default body text
    sm: '15px',    // Secondary body text
    xs: '14px',    // Small text, labels
    xxs: '13px',   // Fine print, captions
    xxxs: '12px',  // Smallest text, metadata
  },
  // Special sizes
  button: {
    lg: '16px',
    md: '15px',
    sm: '14px',
    xs: '13px',
  },
} as const;

export const FONT_WEIGHT = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

export const LINE_HEIGHT = {
  tight: 1.2,
  snug: 1.4,
  normal: 1.6,
  relaxed: 1.8,
} as const;

// Pre-composed typography styles for common use cases
export const TYPOGRAPHY = {
  // Display styles
  displayXL: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.display.xl};
    font-weight: ${FONT_WEIGHT.extrabold};
    line-height: ${LINE_HEIGHT.tight};
  `,
  displayLG: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.display.lg};
    font-weight: ${FONT_WEIGHT.extrabold};
    line-height: ${LINE_HEIGHT.tight};
  `,
  displayMD: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.display.md};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: ${LINE_HEIGHT.tight};
  `,

  // Heading styles
  h1: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.heading.xl};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: ${LINE_HEIGHT.tight};
  `,
  h2: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.heading.lg};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: ${LINE_HEIGHT.tight};
  `,
  h3: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.heading.md};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: ${LINE_HEIGHT.snug};
  `,
  h4: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.heading.sm};
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: ${LINE_HEIGHT.snug};
  `,
  h5: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.heading.xs};
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: ${LINE_HEIGHT.snug};
  `,

  // Body styles
  bodyLG: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.body.lg};
    font-weight: ${FONT_WEIGHT.regular};
    line-height: ${LINE_HEIGHT.normal};
  `,
  body: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.body.md};
    font-weight: ${FONT_WEIGHT.regular};
    line-height: ${LINE_HEIGHT.normal};
  `,
  bodySM: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.body.sm};
    font-weight: ${FONT_WEIGHT.regular};
    line-height: ${LINE_HEIGHT.normal};
  `,
  bodyXS: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.body.xs};
    font-weight: ${FONT_WEIGHT.regular};
    line-height: ${LINE_HEIGHT.normal};
  `,

  // Button styles
  buttonLG: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.button.lg};
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: ${LINE_HEIGHT.tight};
  `,
  button: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.button.md};
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: ${LINE_HEIGHT.tight};
  `,
  buttonSM: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.button.sm};
    font-weight: ${FONT_WEIGHT.semibold};
    line-height: ${LINE_HEIGHT.tight};
  `,

  // Special styles
  caption: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.body.xxs};
    font-weight: ${FONT_WEIGHT.regular};
    line-height: ${LINE_HEIGHT.snug};
  `,
  label: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.body.xs};
    font-weight: ${FONT_WEIGHT.medium};
    line-height: ${LINE_HEIGHT.tight};
  `,
  link: `
    font-family: ${FONT_FAMILY.primary};
    font-size: ${FONT_SIZE.body.md};
    font-weight: ${FONT_WEIGHT.medium};
    line-height: ${LINE_HEIGHT.normal};
  `,
} as const;
